import { useEffect, useRef, useState } from 'react';

// Below this many px of scroll delta since the last direction change, a
// gesture doesn't count as "the user is scrolling up/down" -- filters out
// the sub-pixel jitter of momentum/inertial scrolling and trackpad noise
// that would otherwise flicker chrome in and out on every frame.
const SCROLL_DIRECTION_THRESHOLD = 8;

/**
 * Shared scroll-reactive state for mobile chrome that reacts to the page's
 * own scroll position -- AppHeader's bottom-edge shadow (`scrolled`) and
 * MobileBottomNav's hide-on-scroll-down/show-on-scroll-up (`scrollingDown`).
 * The two are independent consumers of the same underlying scroll tracking,
 * not coupled to each other. `document.body` -- not `window` -- is this
 * app's actual scrolling element (see index.css's html/body rules), so
 * that's what's listened to. Always reports "not scrolling down" at the
 * very top (scrollTop === 0), regardless of the last gesture's direction.
 */
export const useScrollChrome = () => {
  const [scrolled, setScrolled] = useState(false);
  const [scrollingDown, setScrollingDown] = useState(false);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    const scrollEl = document.body;
    const handleScroll = () => {
      const top = scrollEl.scrollTop;
      setScrolled(top > 0);
      if (top <= 0) {
        setScrollingDown(false);
        lastScrollTopRef.current = 0;
        return;
      }
      const delta = top - lastScrollTopRef.current;
      if (Math.abs(delta) > SCROLL_DIRECTION_THRESHOLD) {
        setScrollingDown(delta > 0);
        lastScrollTopRef.current = top;
      }
    };
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, []);

  return { scrolled, scrollingDown };
};
